export type NetworkId = "ethereum" | "bsc" | "tron" | "polygon" | "arbitrum" | "optimism"

export type FlowStepId = "select" | "pay" | "confirm" | "receive"

export type SecurityFeatureId =
  | "coldStorage"
  | "encryption"
  | "antiFraud"
  | "support"
  | "insurance"

export type OrderStatus = "completed" | "processing" | "confirmed"

export type LiveOrder = {
  id: string
  network: NetworkId
  amount: number
  address: string
  status: OrderStatus
  timestamp: string
}

export type TestimonialId = "t1" | "t2" | "t3" | "t4"

export type FaqId = "q1" | "q2" | "q3" | "q4" | "q5" | "q6"

export const USDT_PRICE = 1.0002

export type NetworkSelectId =
  | "ethereum"
  | "bnb"
  | "usdt_trc20"
  | "tron"
  | "usdt_erc20"
  | "polygon"
  | "arbitrum"
  | "optimism"

export type NetworkSelectOption = {
  id: NetworkSelectId
  label: string
  chain: NetworkId
}

/** English-only labels for the purchase form network selector. */
export const networkSelectOptions: NetworkSelectOption[] = [
  { id: "ethereum", label: "Ethereum", chain: "ethereum" },
  { id: "bnb", label: "BNB", chain: "bsc" },
  { id: "usdt_trc20", label: "USDT (TRC20)", chain: "tron" },
  { id: "tron", label: "TRON", chain: "tron" },
  { id: "usdt_erc20", label: "USDT (ERC20)", chain: "ethereum" },
  { id: "polygon", label: "Polygon", chain: "polygon" },
  { id: "arbitrum", label: "Arbitrum", chain: "arbitrum" },
  { id: "optimism", label: "Optimism", chain: "optimism" },
]

export function getNetworkSelectLabel(id: NetworkSelectId): string {
  return networkSelectOptions.find((option) => option.id === id)?.label ?? id
}

export function getNetworkSelectChain(id: NetworkSelectId): NetworkId {
  return networkSelectOptions.find((option) => option.id === id)?.chain ?? "ethereum"
}

export const coreStats = [
  { id: "users", value: 500000, suffix: "+", decimals: 0 },
  { id: "volume", value: 3.2, prefix: "$", suffix: "B", decimals: 1 },
  { id: "countries", value: 180, suffix: "+", decimals: 0 },
  { id: "uptime", value: 99.98, suffix: "%", decimals: 2 },
] as const

export const flowSteps: FlowStepId[] = ["select", "pay", "confirm", "receive"]

export const supportedNetworks: NetworkId[] = [
  "ethereum",
  "bsc",
  "tron",
  "polygon",
  "arbitrum",
  "optimism",
]

export const networkMeta: Record<
  NetworkId,
  { symbol: string; avgTime: string; fee: string }
> = {
  ethereum: { symbol: "ERC-20", avgTime: "~3 min", fee: "$2.50" },
  bsc: { symbol: "BEP-20", avgTime: "~1 min", fee: "$0.30" },
  tron: { symbol: "TRC-20", avgTime: "~30 sec", fee: "$1.00" },
  polygon: { symbol: "POL", avgTime: "~2 min", fee: "$0.10" },
  arbitrum: { symbol: "ARB", avgTime: "~1 min", fee: "$0.50" },
  optimism: { symbol: "OP", avgTime: "~1 min", fee: "$0.40" },
}

export const securityFeatures: SecurityFeatureId[] = [
  "coldStorage",
  "encryption",
  "antiFraud",
  "support",
  "insurance",
]

export const liveOrders: LiveOrder[] = [
  {
    id: "ord-8291",
    network: "ethereum",
    amount: 2500,
    address: "0x7a2f...9c4e",
    status: "completed",
    timestamp: "2 min ago",
  },
  {
    id: "ord-8290",
    network: "tron",
    amount: 10000,
    address: "TXyz...8kLm",
    status: "processing",
    timestamp: "4 min ago",
  },
  {
    id: "ord-8289",
    network: "bsc",
    amount: 500,
    address: "0x3b1c...7f2a",
    status: "confirmed",
    timestamp: "6 min ago",
  },
  {
    id: "ord-8288",
    network: "polygon",
    amount: 1500,
    address: "0x9d4e...1b3c",
    status: "completed",
    timestamp: "8 min ago",
  },
  {
    id: "ord-8287",
    network: "arbitrum",
    amount: 3200,
    address: "0x5f8a...2d7e",
    status: "completed",
    timestamp: "11 min ago",
  },
  {
    id: "ord-8286",
    network: "optimism",
    amount: 750,
    address: "0x1c6b...4a9f",
    status: "processing",
    timestamp: "14 min ago",
  },
  {
    id: "ord-8285",
    network: "ethereum",
    amount: 50000,
    address: "0x8e3d...6c1b",
    status: "confirmed",
    timestamp: "16 min ago",
  },
  {
    id: "ord-8284",
    network: "tron",
    amount: 2000,
    address: "TPqr...3nWx",
    status: "completed",
    timestamp: "19 min ago",
  },
]

export const testimonialIds: TestimonialId[] = ["t1", "t2", "t3", "t4"]

export const faqIds: FaqId[] = ["q1", "q2", "q3", "q4", "q5", "q6"]

export const navLinks = [
  { href: "#buy", key: "buy" },
  { href: "#how-it-works", key: "howItWorks" },
  { href: "#networks", key: "networks" },
  { href: "#security", key: "security" },
  { href: "#faq", key: "faq" },
] as const

export function formatUsdtAmount(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount)
}
